/**
 * Host view of phone check-ins for one game night. Used by the Game Night
 * hub and by the scoring screen.
 *
 * Lists the teams that phones checked in (newest first) and flags likely
 * duplicates. The host can rename a team, merge it into another team on the
 * same night, or reject it. Reject deletes the team row, the same as the
 * existing delete. The parent reloads its teams through `onChanged` and
 * also when the "teams-changed" socket signal arrives.
 */
import { useMemo, useState } from 'react';
import { Badge, Button, Flex, Select, Text, TextField } from '@radix-ui/themes';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { findDuplicates } from '../../utils/checkinDuplicates';
import './CheckInPanel.css';

export interface CheckInTeam {
  id: string;
  name: string;
  playerCount: number;
  canonicalTeamId?: number | null;
  checkedInAt?: string | null;
  leftEarly?: boolean;
}

interface Props {
  teams: CheckInTeam[];
  onChanged: () => void;
  /** Show only teams that phones checked in (default) or every team. */
  phoneOnly?: boolean;
}

export default function CheckInPanel({ teams, onChanged, phoneOnly = true }: Props) {
  const dups = useMemo(() => findDuplicates(teams), [teams]);
  const byId = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);

  const rows = useMemo(() => {
    const list = phoneOnly ? teams.filter((t) => t.checkedInAt) : [...teams];
    return list.sort((a, b) => (b.checkedInAt ?? '').localeCompare(a.checkedInAt ?? ''));
  }, [teams, phoneOnly]);

  if (rows.length === 0) {
    return <Text size="2" color="gray">No phone check-ins yet.</Text>;
  }

  return (
    <div className="cip-list">
      {rows.map((t) => (
        <CheckInRow
          key={t.id}
          team={t}
          others={teams.filter((o) => o.id !== t.id)}
          duplicateOf={(dups.get(t.id) ?? []).map((id) => byId.get(id)?.name ?? '').filter(Boolean)}
          onChanged={onChanged}
        />
      ))}
    </div>
  );
}

function CheckInRow({
  team,
  others,
  duplicateOf,
  onChanged,
}: {
  team: CheckInTeam;
  others: CheckInTeam[];
  duplicateOf: string[];
  onChanged: () => void;
}) {
  const [mode, setMode] = useState<'view' | 'rename' | 'merge'>('view');
  const [draft, setDraft] = useState(team.name);
  const [target, setTarget] = useState<string>('');
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>, failMsg: string) => {
    setBusy(true);
    try {
      await fn();
      setMode('view');
      onChanged();
    } catch {
      toast.error(failMsg);
    } finally {
      setBusy(false);
    }
  };

  const rename = () => {
    const name = draft.trim();
    if (!name || name === team.name) {
      setMode('view');
      return;
    }
    run(() => api.put(`/api/teams/${team.id}`, { name }), 'Could not rename the team');
  };

  const merge = () => {
    if (!target) return;
    const into = others.find((o) => o.id === target);
    if (!window.confirm(`Merge "${team.name}" into "${into?.name}"? "${into?.name}" keeps its name.`)) return;
    run(() => api.post(`/api/teams/${team.id}/merge-into`, { targetId: target }), 'Could not merge the teams');
  };

  const reject = () => {
    if (!window.confirm(`Reject the check-in "${team.name}"? This removes the team from tonight.`)) return;
    run(() => api.delete(`/api/teams/${team.id}`), 'Could not reject the check-in');
  };

  return (
    <div className="cip-row">
      <Flex justify="between" align="start" gap="2" wrap="wrap">
        <Flex direction="column" gap="1" className="cip-main">
          <Text weight="bold" className="cip-name">{team.name}</Text>
          <Flex gap="1" wrap="wrap">
            <Badge color="gray">{team.playerCount} players</Badge>
            {team.canonicalTeamId != null && <Badge color="green">returning</Badge>}
            {duplicateOf.length > 0 && (
              <Badge color="amber">possible duplicate of {duplicateOf.join(', ')}</Badge>
            )}
          </Flex>
        </Flex>
        {mode === 'view' && (
          <Flex gap="1" wrap="wrap">
            <Button size="1" variant="soft" color="gray" onClick={() => { setDraft(team.name); setMode('rename'); }}>
              Rename
            </Button>
            <Button
              size="1"
              variant="soft"
              color="gray"
              disabled={others.length === 0}
              onClick={() => { setTarget(''); setMode('merge'); }}
            >
              Merge
            </Button>
            <Button size="1" variant="soft" color="red" onClick={reject} disabled={busy}>
              Reject
            </Button>
          </Flex>
        )}
      </Flex>

      {mode === 'rename' && (
        <Flex gap="2" mt="2" align="center">
          <TextField.Root
            size="2"
            value={draft}
            maxLength={60}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') rename();
              if (e.key === 'Escape') setMode('view');
            }}
            autoFocus
            className="cip-grow"
            aria-label="New team name"
          />
          <Button size="2" onClick={rename} disabled={busy}>Save</Button>
          <Button size="2" variant="soft" color="gray" onClick={() => setMode('view')}>Cancel</Button>
        </Flex>
      )}

      {mode === 'merge' && (
        <Flex gap="2" mt="2" align="center" wrap="wrap">
          <Text size="2">Merge into</Text>
          <Select.Root value={target} onValueChange={setTarget}>
            <Select.Trigger placeholder="Pick a team" className="cip-grow" />
            <Select.Content>
              {others.map((o) => (
                <Select.Item key={o.id} value={o.id}>{o.name}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          <Button size="2" onClick={merge} disabled={!target || busy}>Merge</Button>
          <Button size="2" variant="soft" color="gray" onClick={() => setMode('view')}>Cancel</Button>
        </Flex>
      )}
    </div>
  );
}
